/**
 * Live data hook: SSE first, polling fallback.
 *
 * The API pushes over Server-Sent Events; if the stream cannot be established
 * (corporate proxy, offline, EventSource unsupported) the hook silently drops to
 * polling the same REST endpoint on the configured interval. Callers get one
 * shape either way and can show which transport is actually in use.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, streamUrl } from './api.ts';

export type Transport = 'connecting' | 'stream' | 'poll' | 'error';

export interface LiveResult<T> {
  data: T | null;
  transport: Transport;
  error: string | null;
  updatedAt: number | null;
  refresh: () => void;
}

export interface UseLiveOptions {
  /** REST endpoint, used for the initial load and for polling. */
  fetchPath: string;
  /** SSE endpoint. Omit to poll only. */
  streamPath?: string;
  /** Named SSE event to listen for. */
  eventName?: string;
  pollSeconds?: number;
  enabled?: boolean;
}

export function useLive<T>(options: UseLiveOptions): LiveResult<T> {
  const { fetchPath, streamPath, eventName = 'leaderboard', pollSeconds = 20, enabled = true } = options;
  const [data, setData] = useState<T | null>(null);
  const [transport, setTransport] = useState<Transport>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const sourceRef = useRef<EventSource | null>(null);
  const timerRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const payload = await api<T>(fetchPath);
      setData(payload);
      setUpdatedAt(Date.now());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
      setTransport('error');
    }
  }, [fetchPath]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    // Always fetch once so the first paint has data even if SSE is slow.
    void load();

    const startPolling = () => {
      if (timerRef.current) return;
      setTransport('poll');
      timerRef.current = window.setInterval(() => {
        // Don't poll a hidden tab — clubhouse screens stay visible, phones don't.
        if (document.visibilityState === 'visible') void load();
      }, Math.max(5, pollSeconds) * 1000);
    };

    if (streamPath && 'EventSource' in window) {
      const source = new EventSource(streamUrl(streamPath));
      sourceRef.current = source;

      source.addEventListener('open', () => {
        if (cancelled) return;
        setTransport('stream');
        if (timerRef.current) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
      });

      source.addEventListener(eventName, (event) => {
        if (cancelled) return;
        try {
          setData(JSON.parse((event as MessageEvent).data) as T);
          setUpdatedAt(Date.now());
          setError(null);
        } catch {
          /* a malformed frame should not kill the stream */
        }
      });

      source.addEventListener('error', () => {
        if (cancelled) return;
        // EventSource retries on its own; polling covers the gap meanwhile.
        startPolling();
      });
    } else {
      startPolling();
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      sourceRef.current?.close();
      sourceRef.current = null;
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [enabled, eventName, fetchPath, load, pollSeconds, streamPath]);

  return { data, transport, error, updatedAt, refresh: () => void load() };
}

/** Read-once fetch with loading and error state, for non-live screens. */
export function useFetch<T>(path: string | null, deps: unknown[] = []): { data: T | null; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api<T>(path)
      .then((payload) => {
        if (!cancelled) {
          setData(payload);
          setError(null);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}
